<script setup lang="ts">
/**
 * Declaration statements + terms checkbox. Legal links sit outside the
 * checkbox label so assistive tech treats them as separate controls.
 */
import { useStore } from 'vuex'

withDefaults(
  defineProps<{
    termsInputId?: string
    /** Compact spacing for modal layouts. */
    compact?: boolean
    /** Hide the panel heading when the page already has a title. */
    hideTitle?: boolean
  }>(),
  {
    termsInputId: 'swiftaid-authorisation-terms',
    compact: false,
    hideTitle: false,
  }
)

const termsAgreed = defineModel<boolean>('termsAgreed', { default: false })

const store = useStore()

const termsLink = import.meta.env.VITE_LEGAL_TERMS_LINK
const privacyLink = import.meta.env.VITE_LEGAL_PRIVACY_LINK

const setTermsModalVisibility = (visibility: boolean) => {
  store.commit('SET_TERMS_MODAL_VISIBILITY', visibility)
}

const setPrivacyModalVisibility = (visibility: boolean) => {
  store.commit('SET_PRIVACY_MODAL_VISIBILITY', visibility)
}

/** Toggle agreement when the copy is clicked, but not when a legal link is. */
const onTermsCopyClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement | null
  if (target?.closest('a')) return
  termsAgreed.value = !termsAgreed.value
}
</script>

<template>
  <section
    class="sa-authorisation-panel"
    :class="{ 'sa-authorisation-panel--compact': compact }"
    :aria-labelledby="hideTitle ? undefined : 'swiftaid-authorisation-title'"
    :aria-label="hideTitle ? $t('LoginRegister.Declaration.Header') : undefined"
    data-testid="authorisation-section"
  >
    <h3
      v-if="!hideTitle"
      id="swiftaid-authorisation-title"
      class="sa-authorisation-panel__title"
    >
      {{ $t('Dashboard.Status.Authorisation.Title') }}
    </h3>
    <p class="sa-authorisation-panel__text">
      {{ $t('Dashboard.Status.Authorisation.Paragraph1') }}
    </p>
    <p class="sa-authorisation-panel__text" data-testid="auth-text">
      {{ $t('Dashboard.Status.Authorisation.AuthText') }}
    </p>

    <div class="sa-authorisation-panel__terms">
      <input
        :id="termsInputId"
        v-model="termsAgreed"
        type="checkbox"
        class="sa-authorisation-panel__checkbox"
        data-testid="authorisation-checkbox"
        :aria-labelledby="`${termsInputId}-text`"
      />
      <i18n-t
        :id="`${termsInputId}-text`"
        keypath="Dashboard.Status.Authorisation.CheckboxLabel"
        tag="span"
        class="sa-authorisation-panel__terms-text"
        scope="global"
        @click="onTermsCopyClick"
      >
        <template #terms>
          <a
            class="sa-link"
            :href="termsLink"
            data-testid="legal-terms-link"
            @click.stop.prevent="setTermsModalVisibility(true)"
          >
            {{ $t('Dashboard.Status.Authorisation.TermsLinkText') }}
          </a>
        </template>
        <template #privacy>
          <a
            class="sa-link"
            :href="privacyLink"
            data-testid="legal-privacy-link"
            @click.stop.prevent="setPrivacyModalVisibility(true)"
          >
            {{ $t('Dashboard.Status.Authorisation.PrivacyLinkText') }}
          </a>
        </template>
      </i18n-t>
    </div>
  </section>
</template>
